import { Badge, type BadgeTone } from "../components/ui/Badge";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { DistributionBar } from "../components/DistributionBar";
import { ErrorState } from "../components/ui/ErrorState";
import { SkeletonScreen } from "../components/ui/Skeleton";
import { coverageStatusLabel, systemScreen } from "../copy/sq";
import { formatDateTime } from "../lib/date";
import { useCoverage } from "../hooks/useApi";
import type { CoverageStatus } from "../types/api";

const COVERAGE_TONE: Record<CoverageStatus, BadgeTone> = {
  good: "good",
  degraded: "warn",
  poor: "bad",
  unknown: "neutral",
};

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <p className="mt-0.5 font-serif text-xl font-semibold text-ink-950">{value}</p>
    </div>
  );
}

export function SystemScreen() {
  const { data, isLoading, isError, error, refetch } = useCoverage();

  if (isLoading) return <SkeletonScreen cards={2} />;

  if (isError) {
    return <ErrorState title={systemScreen.errorTitle} message={error instanceof Error ? error.message : undefined} onRetry={() => refetch()} />;
  }

  if (!data) return null;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <div>
            <h3 className="font-serif text-lg font-semibold text-ink-950">{systemScreen.coverageTitle}</h3>
            {data.run_started_at ? <p className="text-xs text-ink-400">{systemScreen.latestRun(formatDateTime(data.run_started_at))}</p> : null}
          </div>
          <Badge tone={COVERAGE_TONE[data.coverage_status]}>{coverageStatusLabel[data.coverage_status]}</Badge>
        </CardHeader>
        <CardBody className="space-y-5">
          {data.sources_attempted > 0 ? (
            <DistributionBar succeeded={data.sources_succeeded} failed={data.sources_failed} total={data.sources_attempted} />
          ) : (
            <p className="text-sm italic text-ink-400">{systemScreen.noSourcesDue}</p>
          )}

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label={systemScreen.monitored} value={data.monitored_sources} />
            <Stat label={systemScreen.attempted} value={data.sources_attempted} />
            <Stat label={systemScreen.successful} value={data.sources_succeeded} />
            <Stat label={systemScreen.tier1Unhealthy} value={data.tier1_unhealthy_count} />
          </div>

          {data.success_rate !== null ? (
            <p className="text-xs text-ink-400">{systemScreen.successRate(Math.round(data.success_rate * 100))}</p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="font-serif text-lg font-semibold text-ink-950">{systemScreen.outputTitle}</h3>
        </CardHeader>
        <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label={systemScreen.documentsCreated} value={data.documents_created} />
          <Stat label={systemScreen.signalsCreated} value={data.signals_created} />
          <Stat label={systemScreen.eligibleForEditorial} value={data.editorially_eligible_signals ?? "—"} />
          <Stat label={systemScreen.backgroundOnly} value={data.background_only_signals ?? "—"} />
        </CardBody>
      </Card>

      <p className="text-xs text-ink-400">{systemScreen.footerNote}</p>
    </div>
  );
}
